/**
 * Frog Fuel - Mobile Accessibility Launcher Positioning
 *
 * Purpose:
 * 1. Reduce the accessiBe mobile launcher from 45px to 40px
 * 2. Add slightly more spacing from the right/bottom edge
 * 3. Detect when Frog Fuel's sticky Add to Cart panel is visible
 * 4. Move the accessibility launcher above the sticky ATC to prevent overlap
 * 5. Return the launcher to its normal position when the sticky ATC is hidden
 *
 * Mobile only.
 * Desktop behavior remains unchanged.
 */

(() => {
  'use strict';

  const MOBILE_MAX = 768;
  const BUTTON_SIZE = 40;
  const RIGHT_GAP = 10;
  const BOTTOM_GAP = 10;
  const ATC_GAP = 10;

  let ticking = false;

  /**
   * accessiBe renders its launcher inside an open Shadow DOM.
   * Find the actual accessibility launcher button.
   */
  function getAccessibilityButton() {
    const hosts = document.querySelectorAll('access-widget-ui');

    for (const host of hosts) {
      if (!host.shadowRoot) continue;

      const button = host.shadowRoot.querySelector(
        'button[data-testid="acsb-trigger"]'
      );

      if (button) return button;
    }

    return null;
  }

  /**
   * Frog Fuel sticky Add to Cart wrapper.
   */
  function getStickyATC() {
    return document.querySelector('sticky-atc-panel');
  }

  /**
   * Shopify adds a bottom toolbar while previewing themes.
   * On the actual live storefront this normally returns 0.
   */
  function getPreviewBarHeight() {
    const previewBar =
      document.querySelector('#preview-bar-iframe') ||
      document.querySelector('iframe[src*="preview_bar"]') ||
      document.querySelector('iframe[src*="preview-bar"]');

    if (!previewBar) return 0;

    const rect = previewBar.getBoundingClientRect();

    if (
      rect.height > 0 &&
      rect.top < window.innerHeight &&
      rect.bottom > 0
    ) {
      return Math.max(0, window.innerHeight - rect.top);
    }

    return 0;
  }

  function updateAccessibilityPosition() {
    ticking = false;

    const button = getAccessibilityButton();

    // accessiBe may still be loading
    if (!button) return;

    /**
     * Desktop:
     * remove our overrides and leave accessiBe unchanged.
     */
    if (window.innerWidth > MOBILE_MAX) {
      button.style.removeProperty('width');
      button.style.removeProperty('height');
      button.style.removeProperty('right');
      button.style.removeProperty('bottom');
      return;
    }

    /**
     * Mobile launcher size.
     */
    button.style.setProperty(
      'width',
      `${BUTTON_SIZE}px`,
      'important'
    );

    button.style.setProperty(
      'height',
      `${BUTTON_SIZE}px`,
      'important'
    );

    button.style.setProperty(
      'right',
      `${RIGHT_GAP}px`,
      'important'
    );

    /**
     * Sticky ATC collision handling.
     */
    const stickyATC = getStickyATC();

    if (stickyATC) {
      const rect = stickyATC.getBoundingClientRect();

      const visible =
        rect.width > 0 &&
        rect.height > 0 &&
        rect.top < window.innerHeight &&
        rect.bottom > 0;

      if (visible) {
        const occupiedBottomSpace =
          window.innerHeight - rect.top;

        button.style.setProperty(
          'bottom',
          `${Math.ceil(
            occupiedBottomSpace + ATC_GAP
          )}px`,
          'important'
        );

        return;
      }
    }

    /**
     * Normal mobile position.
     */
    const previewOffset = getPreviewBarHeight();

    button.style.setProperty(
      'bottom',
      `${BOTTOM_GAP + previewOffset}px`,
      'important'
    );
  }

  /**
   * Avoid excessive work during scroll/resize.
   */
  function requestUpdate() {
    if (ticking) return;

    ticking = true;

    requestAnimationFrame(
      updateAccessibilityPosition
    );
  }

  window.addEventListener(
    'scroll',
    requestUpdate,
    { passive: true }
  );

  window.addEventListener(
    'resize',
    requestUpdate,
    { passive: true }
  );

  window.addEventListener(
    'orientationchange',
    requestUpdate
  );

  /**
   * accessiBe loads asynchronously.
   * Retry briefly until the launcher becomes available.
   */
  let attempts = 0;

  const initInterval = setInterval(() => {
    attempts++;

    updateAccessibilityPosition();

    if (
      getAccessibilityButton() ||
      attempts >= 40
    ) {
      clearInterval(initInterval);
    }
  }, 250);

  updateAccessibilityPosition();
})();